import type { ContractSchema } from '@libs/data-access/entities'
import type { ApiErrorResponse } from '@libs/data-access/queries'
import type { SelectOption } from '@libs/ui/ds'
import { Alert, Badge, MultiSelect, Select } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { formatErrors } from '@spa/components/FormFields/helpers'
import { QueriesRequestsStatus } from '@spa/features/Queries'
import { getMostRecentDate, getOldestDate } from '@spa/utils/date'
import { isEqual } from 'lodash-es'
import React, { useEffect } from 'react'
import type { FC } from 'react'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import { usePlannableContracts } from '../../hooks'
import { UNASSIGNED_SHIFT } from '../../utils/createShiftArrayPayload'
import { ContractIsntFullyAvailable } from '../Alerts'

import { isContractInFullProgressBetween } from './isContractInFullProgressBetween'

type SelectItemType = {
  label?: string
  value?: number
  badgeColor?: string
  badgeVariant?: string
  highlight?: string
}

type Props = {
  control: Control
  errors: FieldErrors
  disabled: boolean
  isUpdatingWeeklyschedule: boolean
  shiftDateISO: string
  weeklyScheduleTeamId: number
  haveMultipleUserWithError: number
  isCreation: boolean
  valueState: SelectItemType[]
  setValue: Function
  selectedDays: string[]
}
export const UserContractField: FC<Props> = ({
  control,
  errors,
  disabled,
  isUpdatingWeeklyschedule,
  shiftDateISO,
  weeklyScheduleTeamId,
  haveMultipleUserWithError,
  isCreation,
  valueState,
  setValue,
  selectedDays
}) => {
  const { t, tCommon } = usePlanningFormTranslation()
  const queryPlannableContracts = usePlannableContracts(
    shiftDateISO,
    weeklyScheduleTeamId
  )

  const plannableContractsByLocations = queryPlannableContracts.data.reduce(
    (acc, contract) => {
      if (!acc[contract.location_id]) {
        acc[contract.location_id] = []
      }
      acc[contract.location_id].push(contract)
      return acc
    },
    {}
  )

  let locationToHandle: ContractSchema[] = []
  Object.keys(plannableContractsByLocations).forEach(key => {
    locationToHandle = locationToHandle.concat(
      plannableContractsByLocations[key]
    )
  })

  const usersContractOptions: SelectOption[] = queryPlannableContracts.isSuccess
    ? locationToHandle.map(contract => {
        const defaultValueState = {
          label: tCommon('models.fullname', {
            value: {
              firstname: contract.firstname,
              lastname: contract.lastname
            }
          }),
          value: contract.id.toString()
        }

        const isBetweenDate = isContractInFullProgressBetween(
          contract,
          getOldestDate(selectedDays) ?? '',
          getMostRecentDate(selectedDays) ?? ''
        )

        if (!isBetweenDate && selectedDays.length) {
          Object.assign(defaultValueState, {
            badgeColor: 'semantic.red',
            badgeVariant: 'outlined',
            highlight: 'danger'
          })
        }

        return defaultValueState
      })
    : []

  if (!isCreation && queryPlannableContracts.isSuccess) {
    const emptyValue = {
      label: t('component.fieldUserContract.unassignedLabel'),
      value: UNASSIGNED_SHIFT.toString()
    }
    usersContractOptions.unshift(emptyValue)
  }

  // We want the object (label, value, highlight), not just the number value
  const handleChange = (newValues: string[] | string) => {
    const formattedData = Array.isArray(newValues) ? newValues : [newValues]
    const updatedValues = formattedData.map(item => {
      return usersContractOptions.find(option => option.value === item)
    })
    setValue('user_contract', updatedValues)
  }

  // Update highlight state of the values when the options are updated
  useEffect(() => {
    if (queryPlannableContracts.isSuccess && usersContractOptions.length > 0) {
      const updatedValueState = valueState
        .map(value => {
          if (usersContractOptions.length === 0) {
            return value
          }
          const t = usersContractOptions.find(
            option => option.value === value?.value?.toString()
          )
          return t
        })
        .filter(x => !!x)
      // I'm checking if the array are the same or not to avoid useless rerender.
      if (!isEqual(updatedValueState, valueState)) {
        setValue('user_contract', updatedValueState)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersContractOptions, queryPlannableContracts.status])

  const SelectType = isCreation ? MultiSelect : Select

  if (!queryPlannableContracts.isSuccess) {
    return (
      <div className='h-10'>
        <QueriesRequestsStatus
          statuses={[queryPlannableContracts.status]}
          errors={[queryPlannableContracts.error as ApiErrorResponse | null]}
        />
      </div>
    )
  }

  return (
    <>
      <Controller
        name='user_contract'
        control={control}
        render={({ field: { value } }) => (
          <SelectType
            clearable
            searchable
            fillWidth
            dropdownHeight='m'
            dropdownWidth='target'
            options={usersContractOptions}
            className='mb-2'
            onChange={handleChange}
            searchAdapter='fuzzyIncludingGroup'
            placeholder={t('component.fieldUserContract.placeholder')}
            value={
              // Value is an array, in modification we only handle the first value of the array
              isCreation
                ? value.filter(x => !!x).map(x => (x ? x.value : x))
                : value[0]?.value
            }
            errorMessage={formatErrors({
              inputName: 'user_contract',
              errors
            })}
            disabled={disabled}
            label={
              <>
                {t('component.fieldUserContract.title', {
                  count: valueState.length
                })}
                {isCreation && (
                  <Badge
                    size='s'
                    label={valueState.length.toString()}
                    className='ml-2'
                  />
                )}
              </>
            }
          />
        )}
      />
      {isUpdatingWeeklyschedule && (
        <Alert intent='semantic.blue'>
          {t('component.fieldUserContract.trick')}
        </Alert>
      )}
      {haveMultipleUserWithError > 0 && (
        <ContractIsntFullyAvailable isMulti={haveMultipleUserWithError > 1} />
      )}
    </>
  )
}
